import { useEffect, useRef, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { ToastAction } from "@/components/ui/toast";
import { toast } from "@/hooks/use-toast";
import { useDeploymentFreshness, CURRENT_BUILD_ID } from "@/hooks/useDeploymentFreshness";

const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
// DS EDIT 9: "deployment" joins the single update authority — SW signals take
// priority so the same release never produces two notices.
type RefreshPromptMode = "waiting" | "controller-changed" | "deployment" | null;

/** Session marker for reload-loop protection (non-sensitive presentation state only). */
const ACTIVATING_BUILD_KEY = "ds-activating-build";

export function PwaUpdatePrompt() {
  const updateToastRef = useRef<ReturnType<typeof toast> | null>(null);
  const promptModeRef = useRef<RefreshPromptMode>(null);
  const hadControllerOnLoadRef = useRef(false);
  const activatingRef = useRef(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration>();
  const [controllerChanged, setControllerChanged] = useState(false);
  const {
    updateAvailable: deploymentUpdateAvailable,
    meta: deploymentMeta,
    dismiss: dismissDeploymentUpdate,
  } = useDeploymentFreshness();

  // Reload-loop protection: clear the activation marker once we've arrived
  // (or if activation failed and we're still on the old build — allows retry).
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(ACTIVATING_BUILD_KEY)) {
        window.sessionStorage.removeItem(ACTIVATING_BUILD_KEY);
      }
    } catch {
      // sessionStorage unavailable — marker is optional
    }
  }, []);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_swUrl, swRegistration) {
      setRegistration(swRegistration);
    },
    onRegisterError(error) {
      console.error("Service worker registration failed", error);
    },
  });

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    hadControllerOnLoadRef.current = Boolean(navigator.serviceWorker.controller);

    const handleControllerChange = () => {
      // Ignore the first takeover for users who had no controller before the initial install.
      if (!hadControllerOnLoadRef.current) {
        hadControllerOnLoadRef.current = true;
        return;
      }

      setControllerChanged(true);
    };

    navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
    };
  }, []);

  useEffect(() => {
    if (!registration) {
      return;
    }

    const checkForUpdates = () => {
      void registration.update().catch(() => {});
    };

    // Long-lived tabs otherwise tend to stay on the old build until the user revisits.
    checkForUpdates();

    const intervalId = window.setInterval(checkForUpdates, UPDATE_CHECK_INTERVAL_MS);
    const handleVisibilityCheck = () => {
      if (document.visibilityState === "visible") {
        checkForUpdates();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityCheck);
    window.addEventListener("focus", checkForUpdates);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityCheck);
      window.removeEventListener("focus", checkForUpdates);
    };
  }, [registration]);

  const promptMode: RefreshPromptMode = needRefresh
    ? "waiting"
    : controllerChanged
      ? "controller-changed"
      : deploymentUpdateAvailable
        ? "deployment"
        : null;

  useEffect(() => {
    if (!promptMode) {
      updateToastRef.current?.dismiss();
      updateToastRef.current = null;
      promptModeRef.current = null;
      return;
    }

    if (updateToastRef.current && promptModeRef.current === promptMode) {
      return;
    }

    updateToastRef.current?.dismiss();
    promptModeRef.current = promptMode;

    // DS EDIT 9: deployment notices get calm copy + optional plain-text
    // release summary ("What changed?" content, already sanitized+bounded by
    // parseDeploymentMeta). Raw build ids are never shown. Closing the toast
    // (X) acts as "Later" — the same build stays quiet for this tab session.
    const deploymentDescription = deploymentMeta?.summary
      ? `A newer version of Deeply Smart is available.\nWhat changed: ${deploymentMeta.summary}`
      : "A newer version of Deeply Smart is available. Update whenever you're ready.";

    updateToastRef.current = toast({
      title: promptMode === "deployment" ? "An update is ready" : "Update available",
      duration: promptMode === "deployment" ? 30_000 : undefined,
      description:
        promptMode === "waiting"
          ? "A new version is ready. Refresh the page to apply the update."
          : promptMode === "controller-changed"
            ? "A new version has taken control. Refresh the page to load the latest content."
            : deploymentDescription,
      action: (
        <ToastAction
          altText={promptMode === "deployment" ? "Update now" : "Refresh app"}
          onClick={() => {
            if (activatingRef.current) return; // double-invocation guard
            if (promptMode === "waiting") {
              activatingRef.current = true;
              setNeedRefresh(false);
              void updateServiceWorker(true);
              // Bounded activation: if the SW handoff stalls, reload anyway.
              window.setTimeout(() => window.location.reload(), 4000);
              return;
            }
            if (promptMode === "deployment") {
              activatingRef.current = true;
              try {
                window.sessionStorage.setItem(ACTIVATING_BUILD_KEY, deploymentMeta?.buildId ?? "");
              } catch {}
              // Nudge the SW so the reload picks up the fresh precache, then
              // perform ONE controlled reload (user-initiated only).
              if (registration) void registration.update().catch(() => {});
              window.setTimeout(() => window.location.reload(), 300);
              return;
            }

            setControllerChanged(false);
            window.location.reload();
          }}
        >
          {promptMode === "deployment" ? "Update now" : "Refresh"}
        </ToastAction>
      ),
    });

    // "Later": when the deployment toast times out or is closed, suppress
    // that build for the rest of the tab session (a different build re-prompts).
    if (promptMode === "deployment") {
      const toastId = updateToastRef.current;
      window.setTimeout(() => {
        if (updateToastRef.current === toastId && promptModeRef.current === "deployment") {
          dismissDeploymentUpdate();
        }
      }, 30_500);
    }
  }, [controllerChanged, promptMode, setNeedRefresh, updateServiceWorker, deploymentMeta, dismissDeploymentUpdate, registration]);

  useEffect(() => {
    return () => {
      updateToastRef.current?.dismiss();
      updateToastRef.current = null;
      promptModeRef.current = null;
    };
  }, []);

  return null;
}
